import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { useInlineEdit } from '../hooks/useInlineEdit';
import { EmptyState } from './EmptyState';
import { ValidationRules } from '../utils/ValidationRules';
import { RichTextEditor } from '../../../../../../RichTextEditor';
import HtmlView from '../ExperienceHtmlView/HtmlView';
import { T, TPlainTextKey } from '../../../../../../Translations';

interface InlineRichTextProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: Maybe<string>;
  emptyStateText: Maybe<TPlainTextKey>;
  editAriaLabel: Maybe<string>;
  backgroundColor: Maybe<string>;
  intercomTarget: Maybe<string>;
}

function InlineRichText({
  value,
  onChange,
  placeholder,
  emptyStateText,
  editAriaLabel,
  backgroundColor,
  intercomTarget,
}: InlineRichTextProps): JSX.Element {
  const [draftValue, setDraftValue] = useState(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const draftRef = useRef(value);

  const [prevValue, setPrevValue] = useState(value);
  if (value !== prevValue) {
    setPrevValue(value);
    setDraftValue(value);
    draftRef.current = value;
  }

  const handleCommit = useCallback(() => {
    onChange(draftRef.current);
  }, [onChange]);

  const handleEditStart = useCallback(() => {
    setDraftValue(value);
    draftRef.current = value;
  }, [value]);

  const handleCancel = useCallback(() => {
    setDraftValue(value);
    draftRef.current = value;
  }, [value]);

  const {
    isEditing,
    isHovered,
    handleEditClick,
    handleMouseEnter,
    handleMouseLeave,
    finishEdit,
    cancelEdit,
  } = useInlineEdit(handleEditStart, handleCommit, handleCancel);

  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        finishEdit();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        cancelEdit();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditing, finishEdit, cancelEdit]);

  const isEmpty = ValidationRules.isEmpty(value);

  if (isEditing) {
    return (
      <div
        className="relative"
        data-intercom-target={intercomTarget.cata({ Just: (t) => t, Nothing: () => undefined })}
        ref={containerRef}
      >
        <RichTextEditor
          value={draftValue}
          onChange={(newValue) => {
            setDraftValue(newValue);
            draftRef.current = newValue;
          }}
          placeholder={placeholder.cata({ Just: (p) => p, Nothing: () => undefined })}
          readonly={false}
          backgroundColor={backgroundColor.getOrElse(() => '#FFFFFF')}
        />

        <div
          className="mt-2 bg-card border border-border rounded px-3 py-2 flex items-center gap-2"
          style={{ boxShadow: 'var(--elevation-sm)' }}
        >
          <button
            onClick={() => finishEdit()}
            className="px-3 py-1.5 bg-accent text-accent-foreground rounded transition-all hover:opacity-90"
            style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
          >
            <T kind="Save" />
          </button>
          <button
            onClick={() => cancelEdit()}
            className="px-3 py-1.5 bg-background text-foreground border border-border rounded transition-all hover:bg-muted"
            style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
          >
            <T kind="Cancel" />
          </button>
        </div>
      </div>
    );
  }

  if (isEmpty && emptyStateText.isJust()) {
    return (
      <EmptyState
        text={emptyStateText.getOrElse(() => 'Add a collection description')}
        onClick={handleEditClick}
        intercomTarget={intercomTarget}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-intercom-target={intercomTarget.cata({ Just: (t) => t, Nothing: () => undefined })}
    >
      <div
        className="cursor-pointer rounded px-2 py-1.5 -mx-2 -my-1.5 min-h-[60px] transition-colors"
        onClick={isHovered ? handleEditClick : undefined}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'rgba(234, 234, 235, 0.5)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <HtmlView description={value} />
      </div>
    </div>
  );
}

export default observer(InlineRichText);
